import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Info } from "lucide-react";

const PAX_FIELDS = [
  ["first_section_price", "Цена первой секции (с НДС)"],
  ["additional_section_price", "Каждая доп. секция (с НДС)"],
  ["min_price", "Минимальная цена PAX (с НДС)"],
];

const INTERNAL_FIELDS = [
  ["assembler_cost_per_section", "Стоимость сборщика / секция PAX"],
  ["platform_fee", "Комиссия платформы / заказ"],
  ["target_gross_margin", "Целевая валовая маржа (0-1)"],
  ["min_gross_profit_per_job", "Мин. валовая прибыль / заказ"],
  ["safety_buffer", "Защитный буфер цены"],
];

export default function PricingTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/pricing").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return <div className="text-stone-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const setPax = (k, v) => setCfg((c) => ({ ...c, pax: { ...c.pax, [k]: parseFloat(v) || 0 } }));
  const setInternal = (k, v) => setCfg((c) => ({ ...c, internal: { ...c.internal, [k]: parseFloat(v) || 0 } }));
  const setTop = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/admin/pricing", cfg); setCfg(r.data); toast.success("Цены сохранены"); }
    catch (e) { toast.error("Ошибка сохранения"); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full px-3 py-2 rounded-lg border border-stone-300 text-sm font-mono outline-none focus:border-[#1E3A2F]";

  return (
    <div className="space-y-6" data-testid="admin-pricing-tab">
      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
        <Info className="w-4 h-4 mt-0.5 shrink-0" /> Цена PAX = первая секция + (секций − 1) × доп. секция. Ширина и высота — только для справки и никогда не меняют цену. Стандартная цена уже включает двери, ручки, полки, штанги и обычную фурнитуру KOMPLEMENT. Внутренние затраты и маржа видны только администратору.
      </div>

      {/* PAX pricing model */}
      <div className="bg-white border border-stone-200 rounded-2xl p-5">
        <h3 className="font-semibold text-stone-900 mb-4">Модель цен PAX (DKK, с НДС 25%)</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {PAX_FIELDS.map(([k, label]) => (
            <div key={k}>
              <label className="block text-xs text-stone-500 mb-1">{label}</label>
              <input type="number" step="1" className={inputCls} value={cfg.pax[k]} onChange={(e) => setPax(k, e.target.value)} data-testid={`price-${k}`} />
            </div>
          ))}
          <div>
            <label className="block text-xs text-stone-500 mb-1">Ставка НДС (0-1)</label>
            <input type="number" step="0.01" className={inputCls} value={cfg.vat_rate} onChange={(e) => setTop("vat_rate", parseFloat(e.target.value) || 0)} data-testid="price-vat-rate" />
          </div>
        </div>
        <p className="text-xs text-stone-400 mt-3">Предпросмотр: 1 секция = {cfg.pax.first_section_price} · 2 = {cfg.pax.first_section_price + cfg.pax.additional_section_price} · 3 = {cfg.pax.first_section_price + 2 * cfg.pax.additional_section_price} DKK</p>
      </div>

      {/* Internal cost assumptions */}
      <div className="bg-[#1E3A2F]/5 border border-[#1E3A2F]/15 rounded-2xl p-5">
        <h3 className="font-semibold text-[#1E3A2F] mb-1">Внутренние допущения по себестоимости (ТОЛЬКО АДМИН)</h3>
        <p className="text-xs text-stone-500 mb-4">Никогда не показывается клиентам. Используется для расчёта ориентировочной себестоимости, валовой прибыли и маржи.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {INTERNAL_FIELDS.map(([k, label]) => (
            <div key={k}>
              <label className="block text-xs text-stone-500 mb-1">{label}</label>
              <input type="number" step="0.01" className={inputCls} value={cfg.internal[k]} onChange={(e) => setInternal(k, e.target.value)} data-testid={`internal-${k}`} />
            </div>
          ))}
        </div>
      </div>

      <button onClick={save} disabled={saving} className="px-6 py-3 rounded-full bg-[#1E3A2F] text-white font-semibold disabled:opacity-60" data-testid="pricing-save-btn">
        {saving ? "Сохранение…" : "Сохранить цены"}
      </button>
    </div>
  );
}
